import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { 
  Truck, 
  Users, 
  MapPin, 
  AlertTriangle, 
  CheckCircle, 
  FileText,
  Calendar,
  RefreshCcw,
  Upload,
  ClipboardCheck
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ComplianceStatusCard } from '../compliance';
import { ExpiringDocumentsWidget } from '../documents';
import RecentActivityFeed from './RecentActivityFeed';
import companyService from '../../services/companyService';

const resolveResponseData = (response, key) => {
  if (!response) {
    return null;
  }

  if (response.data && typeof response.data === 'object') {
    return key ? response.data[key] : response.data;
  }

  return key ? response[key] : response;
};

const DEFAULT_METRIC_CARDS = [
  {
    title: 'Active Trucks',
    value: '24',
    change: '+2',
    changeType: 'positive',
    icon: Truck,
    color: 'primary',
    description: 'Currently on road'
  },
  {
    title: 'Total Drivers',
    value: '48',
    change: '+3',
    changeType: 'positive',
    icon: Users,
    color: 'success',
    description: 'Available drivers'
  },
  {
    title: 'Active Routes',
    value: '12',
    change: '-1',
    changeType: 'negative',
    icon: MapPin,
    color: 'warning',
    description: 'In progress'
  },
  {
    title: 'Critical Alerts',
    value: '3',
    change: '+1',
    changeType: 'negative',
    icon: AlertTriangle,
    color: 'danger',
    description: 'Require attention'
  }
];

const DEFAULT_COMPLIANCE_METRICS = [
  {
    title: 'DOT Inspections',
    compliant: 42,
    total: 48,
    percentage: 88,
    dueDate: '15 days',
    status: 'good'
  },
  {
    title: 'CDL Renewals',
    compliant: 44,
    total: 48,
    percentage: 92,
    dueDate: '32 days',
    status: 'excellent'
  },
  {
    title: 'Vehicle Registrations',
    compliant: 22,
    total: 24,
    percentage: 92,
    dueDate: '8 days',
    status: 'excellent'
  },
  {
    title: 'Insurance Policies',
    compliant: 23,
    total: 24,
    percentage: 96,
    dueDate: '45 days',
    status: 'excellent'
  }
];

const determineStatusFromPercentage = (percentage) => {
  if (percentage >= 90) return 'excellent';
  if (percentage >= 75) return 'good';
  if (percentage >= 60) return 'warning';
  return 'critical';
};

const toDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value.toDate === 'function') {
    try {
      const parsed = value.toDate();
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    } catch (error) {
      return null;
    }
  }

  if (typeof value === 'object') {
    const seconds = typeof value.seconds === 'number' ? value.seconds : value._seconds;
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : value._nanoseconds;

    if (typeof seconds === 'number') {
      const millis = seconds * 1000 + Math.floor((nanos || 0) / 1_000_000);
      const parsed = new Date(millis);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }

  return null;
};

const toIsoString = (value) => {
  const date = toDateValue(value);
  return date ? date.toISOString() : null;
};

const extractEntityDetailsFromDocument = (doc = {}) => {
  const entityType = doc.category
    || (doc.driverId ? 'driver' : doc.vehicleId ? 'vehicle' : 'company');

  const raw = doc.raw || {};
  const driver = raw.driver || raw.driverProfile || {};
  const vehicle = raw.vehicle || raw.vehicleProfile || {};
  const company = raw.company || {};

  const possibleNames = [
    doc.entityName,
    doc.entityDisplayName,
    doc.driverName,
    doc.vehicleName,
    driver.fullName,
    [driver.firstName, driver.lastName].filter(Boolean).join(' ').trim(),
    vehicle.displayName,
    vehicle.unitNumber,
    vehicle.plateNumber,
    company.name,
    company.displayName
  ].filter(name => typeof name === 'string' && name.trim().length > 0);

  const entityName = possibleNames.length > 0
    ? possibleNames[0]
    : entityType === 'driver'
      ? `Driver ${doc.driverId || ''}`.trim()
      : entityType === 'vehicle'
        ? `Vehicle ${doc.vehicleId || ''}`.trim()
        : doc.companyId ? `Company ${doc.companyId}` : 'Company';

  return {
    entityType,
    entityName
  };
};

const DashboardOverview = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [activitySummary, setActivitySummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [dashboardResponse, activityResponse] = await Promise.all([
        companyService.getDashboardData(),
        companyService.getActivitySummary()
      ]);

      setDashboardData(resolveResponseData(dashboardResponse, 'dashboard'));
      setActivitySummary(resolveResponseData(activityResponse, 'activitySummary'));
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const statistics = dashboardData?.statistics || null;

  const metricCards = useMemo(() => {
    if (!statistics) {
      return DEFAULT_METRIC_CARDS;
    }

    const totalVehicles = statistics.vehicles?.total ?? 0;
    const activeVehicles = statistics.vehicles?.active ?? 0;
    const maintenanceVehicles = statistics.vehicles?.maintenance ?? 0;
    const outOfServiceVehicles = statistics.vehicles?.outOfService ?? 0;

    const totalDrivers = statistics.drivers?.total ?? 0;
    const activeDrivers = statistics.drivers?.active ?? 0;
    const unavailableDrivers = (statistics.drivers?.suspended ?? 0)
      + (statistics.drivers?.inactive ?? 0)
      + (statistics.drivers?.terminated ?? 0);

    const documentsTotal = statistics.documents?.total ?? 0;
    const documentsExpiring = statistics.documents?.expiring ?? 0;
    const documentsExpired = statistics.documents?.expired ?? 0;

    const complianceScore = statistics.compliance?.score ?? 0;
    const complianceCompliant = statistics.compliance?.compliantItems ?? 0;
    const complianceTotal = statistics.compliance?.totalItems ?? 0;
    const complianceLevel = statistics.compliance?.level
      || determineStatusFromPercentage(complianceScore);

    const unavailableVehicles = maintenanceVehicles + outOfServiceVehicles;

    return [
      {
        title: 'Active Vehicles',
        value: activeVehicles.toString(),
        change: `${unavailableVehicles} unavailable`,
        changeType: unavailableVehicles > 0 ? 'negative' : 'positive',
        icon: Truck,
        color: unavailableVehicles > 0 ? 'warning' : 'primary',
        description: `${totalVehicles} total vehicles`
      },
      {
        title: 'Active Drivers',
        value: activeDrivers.toString(),
        change: `${unavailableDrivers} unavailable`,
        changeType: unavailableDrivers > 0 ? 'negative' : 'positive',
        icon: Users,
        color: unavailableDrivers > 0 ? 'warning' : 'success',
        description: `${totalDrivers} total drivers`
      },
      {
        title: 'Expiring Documents',
        value: documentsExpiring.toString(),
        change: `${documentsExpired} expired`,
        changeType: documentsExpired > 0 || documentsExpiring > 0 ? 'negative' : 'positive',
        icon: FileText,
        color: documentsExpired > 0 ? 'danger' : documentsExpiring > 0 ? 'warning' : 'success',
        description: `${documentsTotal} documents tracked`
      },
      {
        title: 'Compliance Score',
        value: `${complianceScore}%`,
        change: `${complianceCompliant}/${complianceTotal || 0} checks compliant`,
        changeType: complianceScore >= 75 ? 'positive' : 'negative',
        icon: CheckCircle,
        color: complianceScore >= 90
          ? 'success'
          : complianceScore >= 75
            ? 'primary'
            : complianceScore >= 60
              ? 'warning'
              : 'danger',
        description: `Level: ${complianceLevel}`
      }
    ];
  }, [statistics]);

  const complianceMetrics = useMemo(() => {
    if (!statistics) {
      return DEFAULT_COMPLIANCE_METRICS;
    }

    const driverTotal = statistics.drivers?.total ?? 0;
    const driverUnavailable = (statistics.drivers?.suspended ?? 0)
      + (statistics.drivers?.inactive ?? 0)
      + (statistics.drivers?.terminated ?? 0);
    const driverCompliant = Math.max(driverTotal - driverUnavailable, 0);
    const driverPercentage = driverTotal > 0
      ? Math.round((driverCompliant / driverTotal) * 100)
      : 100;

    const vehicleTotal = statistics.vehicles?.total ?? 0;
    const vehicleUnavailable = (statistics.vehicles?.maintenance ?? 0)
      + (statistics.vehicles?.outOfService ?? 0)
      + (statistics.vehicles?.retired ?? 0);
    const vehicleCompliant = Math.max(vehicleTotal - vehicleUnavailable, 0);
    const vehiclePercentage = vehicleTotal > 0
      ? Math.round((vehicleCompliant / vehicleTotal) * 100)
      : 100;

    const documentTotal = statistics.documents?.total ?? 0;
    const documentCompliant = statistics.documents?.active ?? 0;
    const documentPercentage = documentTotal > 0
      ? Math.round((documentCompliant / documentTotal) * 100)
      : 100;
    const documentExpiring = statistics.documents?.expiring ?? 0;
    const documentExpired = statistics.documents?.expired ?? 0;

    const compliance = statistics.compliance || {};
    const overallPercentage = compliance.totalItems > 0
      ? Math.round((compliance.compliantItems / compliance.totalItems) * 100)
      : compliance.score ?? 0;

    return [
      {
        key: 'overall-compliance',
        title: 'Overall Compliance',
        compliant: compliance.compliantItems ?? 0,
        total: compliance.totalItems ?? 0,
        percentage: overallPercentage,
        dueDate: `${documentExpiring} renewals upcoming`,
        status: determineStatusFromPercentage(overallPercentage),
        expiring: documentExpiring,
        expired: documentExpired
      },
      {
        key: 'driver-compliance',
        title: 'Driver Readiness',
        compliant: driverCompliant,
        total: driverTotal,
        percentage: driverPercentage,
        dueDate: `${statistics.drivers?.suspended ?? 0} suspended`,
        status: determineStatusFromPercentage(driverPercentage),
        expiring: statistics.drivers?.inactive ?? 0,
        expired: statistics.drivers?.terminated ?? 0
      },
      {
        key: 'vehicle-compliance',
        title: 'Vehicle Availability',
        compliant: vehicleCompliant,
        total: vehicleTotal,
        percentage: vehiclePercentage,
        dueDate: `${statistics.vehicles?.maintenance ?? 0} in maintenance`,
        status: determineStatusFromPercentage(vehiclePercentage),
        expiring: statistics.vehicles?.maintenance ?? 0,
        expired: statistics.vehicles?.outOfService ?? 0
      },
      {
        key: 'document-compliance',
        title: 'Document Health',
        compliant: documentCompliant,
        total: documentTotal,
        percentage: documentPercentage,
        dueDate: `${documentExpiring} expiring soon`,
        status: determineStatusFromPercentage(documentPercentage),
        expiring: documentExpiring,
        expired: documentExpired
      }
    ];
  }, [statistics]);

  const expiringDocuments = useMemo(() => {
    const docs = dashboardData?.expiringDocuments;
    if (!Array.isArray(docs)) {
      return [];
    }

    return docs
      .map((doc, index) => {
        const { entityName, entityType } = extractEntityDetailsFromDocument(doc);
        const expirationDate = toIsoString(doc.expirationDate);

        if (!expirationDate) {
          return null;
        }

        return {
          id: doc.id || doc.documentId || `${doc.documentType || 'document'}-${index}`,
          title: doc.title || doc.documentDisplayName || doc.documentType || 'Document',
          type: doc.documentType || 'document',
          entityName,
          entityType,
          expirationDate,
          issueDate: toIsoString(doc.issueDate),
          documentNumber: doc.documentNumber || '',
          issuingAuthority: doc.issuingAuthority || doc.issuingState || '',
          status: doc.status,
          isRequired: doc.isRequired,
          raw: doc
        };
      })
      .filter(Boolean);
  }, [dashboardData]);

  const activities = useMemo(() => {
    const now = new Date();
    const activityItems = [];

    (dashboardData?.recentUsers || []).forEach((user, index) => {
      const timestamp = toIsoString(user.createdAt || user.updatedAt) || new Date(now.getTime() - index * 30000).toISOString();
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.displayName || user.email || 'New team member';
      const metadata = {};
      if (user.email) metadata.email = user.email;
      if (user.role) metadata.role = user.role;
      if (user.phone) metadata.phone = user.phone;
      metadata.createdAt = timestamp;

      activityItems.push({
        id: `user-${user.id || user.uid || index}`,
        type: 'system_change',
        title: `${name} added to company`,
        description: user.role ? `Role: ${user.role}` : 'New team member onboarding completed',
        entityName: name,
        entityType: 'company',
        timestamp,
        status: 'success',
        metadata
      });
    });

    expiringDocuments.forEach((doc, index) => {
      const expirationDate = doc.expirationDate;
      const timestamp = doc.raw?.updatedAt ? toIsoString(doc.raw.updatedAt) : expirationDate || new Date(now.getTime() - index * 45000).toISOString();
      const daysUntilExpiration = expirationDate ? Math.ceil((new Date(expirationDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
      const isExpired = doc.status === 'expired' || (daysUntilExpiration !== null && daysUntilExpiration < 0);
      const metadata = {};
      if (expirationDate) metadata.expirationDate = expirationDate;
      if (doc.documentNumber) metadata.documentNumber = doc.documentNumber;
      if (doc.isRequired !== undefined) metadata.isRequired = doc.isRequired;
      if (doc.issuingAuthority) metadata.issuingAuthority = doc.issuingAuthority;
      if (daysUntilExpiration !== null) metadata.daysUntilExpiration = daysUntilExpiration;

      activityItems.push({
        id: `document-${doc.id}-${index}`,
        type: isExpired ? 'document_expired' : 'document_expiry_warning',
        title: `${doc.title} ${isExpired ? 'expired' : 'expiring soon'}`,
        description: `${doc.entityName} • ${doc.documentNumber ? `#${doc.documentNumber}` : doc.type}`,
        entityName: doc.entityName,
        entityType: doc.entityType,
        timestamp,
        status: isExpired ? 'error' : 'warning',
        documentType: doc.type,
        metadata
      });
    });

    (dashboardData?.alerts || []).forEach((alert, index) => {
      const timestamp = new Date(now.getTime() - (index + 1) * 60000).toISOString();
      const metadata = { priority: alert.priority, generatedAt: timestamp };

      activityItems.push({
        id: `alert-${index}`,
        type: alert.type === 'warning' ? 'compliance_check' : 'system_change',
        title: alert.title,
        description: alert.message,
        entityName: 'System Alert',
        entityType: 'system',
        timestamp,
        status: alert.type === 'warning' ? 'warning' : 'info',
        metadata
      });
    });

    return activityItems
      .filter(activity => Boolean(activity?.timestamp))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [dashboardData, expiringDocuments]);

  const quickActions = useMemo(() => [
    { 
      title: 'Add Driver', 
      icon: Users, 
      color: 'primary',
      description: statistics
        ? `${statistics.drivers?.total ?? 0} drivers on file`
        : 'Add new driver to system',
      onClick: () => navigate('/drivers?action=add')
    },
    { 
      title: 'Add Vehicle', 
      icon: Truck, 
      color: 'success',
      description: statistics
        ? `${statistics.vehicles?.total ?? 0} vehicles registered`
        : 'Register new vehicle',
      onClick: () => navigate('/vehicles?action=add')
    },
    { 
      title: 'Start Inspection', 
      icon: ClipboardCheck, 
      color: 'warning',
      description: statistics
        ? `${statistics.vehicles?.maintenance ?? 0} awaiting inspection`
        : 'Begin vehicle inspection',
      onClick: () => navigate('/vehicles?action=inspect')
    },
    { 
      title: 'Upload Document', 
      icon: Upload, 
      color: 'success',
      description: statistics
        ? `${statistics.documents?.active ?? 0} active documents`
        : 'Upload compliance documents',
      onClick: () => navigate('/documents?action=upload')
    }
  ], [activitySummary, navigate, statistics]);


  const getColorClasses = (color) => {
    const colorMap = {
      primary: 'text-primary-600 bg-primary-100',
      success: 'text-success-600 bg-success-100',
      warning: 'text-warning-600 bg-warning-100',
      danger: 'text-danger-600 bg-danger-100',
      slate: 'text-slate-600 bg-slate-100'
    };
    return colorMap[color] || colorMap.slate;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Dashboard Overview</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">Monitor your fleet operations and compliance status</p>
        </div>
        <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
          <button className="flex items-center justify-center px-3 sm:px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors duration-200 text-sm">
            <Calendar className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Last 30 days</span>
            <span className="sm:hidden">30 days</span>
          </button>
          <button
            type="button"
            onClick={fetchDashboardData}
            disabled={isLoading}
            className={`flex items-center justify-center px-3 sm:px-4 py-2 rounded-lg transition-colors duration-200 text-sm ${
              isLoading
                ? 'bg-primary-500 text-white cursor-not-allowed'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            <RefreshCcw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {(isLoading || error) && (
        <div className="space-y-2">
          {isLoading && (
            <div className="text-sm text-slate-500">Loading latest metrics...</div>
          )}
          {error && (
            <div className="text-sm text-danger-700 bg-danger-50 border border-danger-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}
        </div>
      )}

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricCards.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div key={index} className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">{metric.title}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{metric.value}</p>
                  <div className="flex items-center mt-2">
                    <span className={`text-sm font-medium ${
                      metric.changeType === 'positive' ? 'text-success-600' : 'text-danger-600'
                    }`}>
                      {metric.change}
                    </span>
                    <span className="text-sm text-slate-500 ml-1">current snapshot</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{metric.description}</p>
                </div>
                <div className={`p-3 rounded-lg ${getColorClasses(metric.color)}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Compliance Section */}
      <div className="grid grid-cols-1 gap-6">
        {/* Compliance Status Card */}
        <ComplianceStatusCard 
          complianceData={complianceMetrics}
          showTrend={true}
          showDetails={true}
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-slate-900 mb-6">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <button
                key={index}
                onClick={() => action.onClick()}
                className="flex flex-col items-center p-4 border-2 border-dashed border-slate-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-all duration-200 group"
              >
                <div className={`p-3 rounded-lg ${getColorClasses(action.color)} group-hover:bg-primary-200 transition-colors duration-200`}>
                  <Icon className="h-6 w-6" />
                </div>
                <span className="text-sm font-medium text-slate-700 mt-2 text-center">{action.title}</span>
                <span className="text-xs text-slate-500 mt-1 text-center">{action.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Expiring Documents Widget */}
      <ExpiringDocumentsWidget 
        documents={expiringDocuments}
        useSampleData={false}
        onDocumentClick={(document) => {
          console.log('Document clicked:', document);
          // Handle document click - could open modal, navigate to detail page, etc.
        }}
        showActions={true}
        maxItems={8}
      />

      {/* Recent Activity Feed */}
      <RecentActivityFeed 
        activities={activities}
        useSampleData={false}
        maxItems={10}
        showFilters={true}
        className="mt-6"
      />

    </div>
  );
};

export default DashboardOverview;
